// ============================================================================
// Parameter Buffer — Atomic Cross-Thread State
// ============================================================================
// Main Thread writes with Atomics.store (never blocks)
// Worker reads with Atomics.load (never blocks)
//
// Layout: [Error Region: 256 bytes] [Fields aligned per C ABI rules]
//
// For Float32: we store as Int32 via Atomics (Float32 has no Atomics support)
// and reinterpret on read. This gives us atomic float reads/writes.
import { ERROR_REGION_SIZE, ERROR_CODE_OFFSET, ERROR_LENGTH_OFFSET, ERROR_MESSAGE_OFFSET, MAX_ERROR_MESSAGE_LENGTH, } from '../types.js';
const TYPE_SIZE = {
    Uint8: 1, Uint16: 2, Uint32: 4, Int8: 1, Int16: 2, Int32: 4, Float32: 4, Float64: 8,
};
const TYPE_ALIGN = {
    Uint8: 1, Uint16: 2, Uint32: 4, Int8: 1, Int16: 2, Int32: 4, Float32: 4, Float64: 8,
};
/**
 * Compute C-style aligned layout for parameter fields.
 *
 * IMPORTANT: Float64 is banned from the Parameter Buffer. JavaScript Atomics
 * only supports 32-bit operations. A Float64 write is two separate 32-bit
 * writes under the hood, which means a concurrent read can see half of the
 * old value and half of the new value (torn read). This is not a theoretical
 * concern — it produces real data corruption under cross-thread contention.
 * Use Float32 instead (sufficient for all UI parameters).
 *
 * Layout strategy:
 * 1. If accessGroups are provided, fields within the same group are placed
 *    adjacent in memory to minimize cache line bouncing. Fields written
 *    together (e.g., cursorX + cursorY + cursorDown) share a cache line.
 * 2. Within each group, fields are sorted by descending alignment for
 *    optimal packing (standard C struct rule).
 * 3. Groups are laid out in declaration order, with cache-line alignment
 *    (64-byte boundary) between groups to prevent false sharing.
 *
 * Without access groups, falls back to simple descending-alignment sort.
 */
export function computeParamLayout(fields, accessGroups) {
    // Validate: reject Float64
    for (const [name, field] of Object.entries(fields)) {
        if (field.type === 'Float64') {
            throw new Error(`Parameter "${name}" uses Float64, which is not safe for cross-thread SharedArrayBuffer access. ` +
                `JavaScript Atomics only supports 32-bit operations. A Float64 read/write is two 32-bit ` +
                `operations and can produce torn values under concurrency. Use Float32 instead.`);
        }
    }
    const result = [];
    let cursor = ERROR_REGION_SIZE;
    if (accessGroups && accessGroups.length > 0) {
        const placed = new Set();
        for (const group of accessGroups) {
            // Align to cache line boundary between groups (64 bytes)
            if (result.length > 0) {
                const cacheLineRemainder = cursor % 64;
                if (cacheLineRemainder !== 0)
                    cursor += 64 - cacheLineRemainder;
            }
            // Sort group members by descending alignment
            const groupFields = group
                .filter(name => name in fields && !placed.has(name))
                .map(name => ({ name, ...fields[name] }))
                .sort((a, b) => TYPE_ALIGN[b.type] - TYPE_ALIGN[a.type]);
            for (const entry of groupFields) {
                const alignment = TYPE_ALIGN[entry.type];
                const byteSize = TYPE_SIZE[entry.type];
                const remainder = cursor % alignment;
                if (remainder !== 0)
                    cursor += alignment - remainder;
                result.push({ name: entry.name, type: entry.type, offset: cursor, byteSize, default: entry.default ?? 0 });
                cursor += byteSize;
                placed.add(entry.name);
            }
        }
        // Place remaining ungrouped fields
        const remaining = Object.entries(fields)
            .filter(([name]) => !placed.has(name))
            .sort((a, b) => TYPE_ALIGN[b[1].type] - TYPE_ALIGN[a[1].type]);
        if (remaining.length > 0 && result.length > 0) {
            const cacheLineRemainder = cursor % 64;
            if (cacheLineRemainder !== 0)
                cursor += 64 - cacheLineRemainder;
        }
        for (const [name, field] of remaining) {
            const alignment = TYPE_ALIGN[field.type];
            const byteSize = TYPE_SIZE[field.type];
            const remainder = cursor % alignment;
            if (remainder !== 0)
                cursor += alignment - remainder;
            result.push({ name, type: field.type, offset: cursor, byteSize, default: field.default ?? 0 });
            cursor += byteSize;
        }
    }
    else {
        // No groups — simple descending-alignment sort
        const sorted = Object.entries(fields).sort((a, b) => TYPE_ALIGN[b[1].type] - TYPE_ALIGN[a[1].type]);
        for (const [name, field] of sorted) {
            const alignment = TYPE_ALIGN[field.type];
            const byteSize = TYPE_SIZE[field.type];
            const remainder = cursor % alignment;
            if (remainder !== 0)
                cursor += alignment - remainder;
            result.push({ name, type: field.type, offset: cursor, byteSize, default: field.default ?? 0 });
            cursor += byteSize;
        }
    }
    return result;
}
// --- Reinterpret helpers ---
const _f32 = new Float32Array(1);
const _i32 = new Int32Array(_f32.buffer);
function floatToInt(f) {
    _f32[0] = f;
    return _i32[0];
}
function intToFloat(i) {
    _i32[0] = i;
    return _f32[0];
}
// --- Atomic sub-word read helpers ---
// DataView.getUint8/getUint16 are NOT atomic on SharedArrayBuffer.
// On weakly-ordered architectures (ARM / Apple Silicon), a non-atomic read
// can return a stale value even after the writer has published via Atomics.store.
// We must Atomics.load the containing int32 and extract the sub-word with bit masking.
function atomicReadUint8(int32, byteOffset) {
    const wordIndex = byteOffset >> 2;
    const bytePos = byteOffset & 3;
    const word = Atomics.load(int32, wordIndex);
    return (word >>> (bytePos * 8)) & 0xFF;
}
function atomicReadInt8(int32, byteOffset) {
    const val = atomicReadUint8(int32, byteOffset);
    return val > 127 ? val - 256 : val;
}
function atomicReadUint16(int32, byteOffset) {
    const wordIndex = byteOffset >> 2;
    const bytePos = byteOffset & 2; // 0 or 2
    const word = Atomics.load(int32, wordIndex);
    return (word >>> (bytePos * 8)) & 0xFFFF;
}
function atomicReadInt16(int32, byteOffset) {
    const val = atomicReadUint16(int32, byteOffset);
    return val > 32767 ? val - 65536 : val;
}
/**
 * ParamWriter — used by the Main Thread to write parameter values.
 * All 32-bit aligned writes use Atomics.store for cross-thread visibility.
 */
export class ParamWriter {
    int32;
    fields;
    base;
    constructor(sab, baseOffset, layout) {
        this.int32 = new Int32Array(sab);
        this.base = baseOffset;
        this.fields = new Map(layout.map(f => [f.name, f]));
        // Write defaults
        for (const f of layout)
            this.write(f.name, f.default);
    }
    write(name, value) {
        const f = this.fields.get(name);
        if (!f)
            return;
        const off = this.base + f.offset;
        switch (f.type) {
            case 'Uint8':
            case 'Int8':
                // Sub-word: pack into aligned 32-bit slot via Atomics
                this._writeSubWord8(off, value);
                break;
            case 'Uint16':
            case 'Int16':
                this._writeSubWord16(off, value);
                break;
            case 'Uint32':
                Atomics.store(this.int32, off >> 2, value | 0);
                break;
            case 'Int32':
                Atomics.store(this.int32, off >> 2, value);
                break;
            case 'Float32':
                Atomics.store(this.int32, off >> 2, floatToInt(value));
                break;
            case 'Float64':
                // UNREACHABLE: computeParamLayout rejects Float64.
                // Defense-in-depth: throw if somehow reached.
                throw new Error('Float64 is not supported in the Parameter Buffer (torn reads under concurrency). Use Float32.');
        }
    }
    read(name) {
        const f = this.fields.get(name);
        if (!f)
            return 0;
        const off = this.base + f.offset;
        switch (f.type) {
            case 'Uint8': return atomicReadUint8(this.int32, off);
            case 'Int8': return atomicReadInt8(this.int32, off);
            case 'Uint16': return atomicReadUint16(this.int32, off);
            case 'Int16': return atomicReadInt16(this.int32, off);
            case 'Uint32': return Atomics.load(this.int32, off >> 2) >>> 0;
            case 'Int32': return Atomics.load(this.int32, off >> 2);
            case 'Float32': return intToFloat(Atomics.load(this.int32, off >> 2));
            case 'Float64':
                throw new Error('Float64 is not supported in the Parameter Buffer. Use Float32.');
            default: return 0;
        }
    }
    /**
     * Atomic write for sub-word (8-bit) values using read-modify-write on the containing int32.
     *
     * SINGLE-PRODUCER INVARIANT: This CAS loop is correct only when there is a single
     * writer thread (the main thread). If two threads call write() concurrently on
     * different bytes within the same int32 word, the CAS loop will retry correctly,
     * but under heavy contention this degrades to a spin-lock. The framework assumes
     * all setParam() calls originate from a single thread.
     */
    _writeSubWord8(byteOffset, value) {
        const wordIndex = byteOffset >> 2;
        const bytePos = byteOffset & 3;
        const mask = ~(0xFF << (bytePos * 8));
        const bits = (value & 0xFF) << (bytePos * 8);
        let oldVal;
        let newVal;
        do {
            oldVal = Atomics.load(this.int32, wordIndex);
            newVal = (oldVal & mask) | bits;
        } while (Atomics.compareExchange(this.int32, wordIndex, oldVal, newVal) !== oldVal);
    }
    /** Atomic write for sub-word (16-bit) values */
    _writeSubWord16(byteOffset, value) {
        const wordIndex = byteOffset >> 2;
        const bytePos = byteOffset & 2; // 0 or 2
        const mask = ~(0xFFFF << (bytePos * 8));
        const bits = (value & 0xFFFF) << (bytePos * 8);
        let oldVal;
        let newVal;
        do {
            oldVal = Atomics.load(this.int32, wordIndex);
            newVal = (oldVal & mask) | bits;
        } while (Atomics.compareExchange(this.int32, wordIndex, oldVal, newVal) !== oldVal);
    }
    getDescriptors() {
        return Array.from(this.fields.values());
    }
    getField(name) {
        return this.fields.get(name);
    }
}
/**
 * ParamReader — used by the Worker/Engine to read parameter values.
 * All reads are atomic, including sub-word (8/16-bit) types which are read
 * by Atomics.load on the containing int32 and extracted via bit masking.
 * This ensures correct visibility on weakly-ordered architectures (ARM).
 */
export class ParamReader {
    int32;
    fields;
    base;
    constructor(sab, baseOffset, layout) {
        this.int32 = new Int32Array(sab);
        this.base = baseOffset;
        this.fields = new Map(layout.map(f => [f.name, f]));
    }
    read(name) {
        const f = this.fields.get(name);
        if (!f)
            return 0;
        const off = this.base + f.offset;
        switch (f.type) {
            case 'Uint8': return atomicReadUint8(this.int32, off);
            case 'Int8': return atomicReadInt8(this.int32, off);
            case 'Uint16': return atomicReadUint16(this.int32, off);
            case 'Int16': return atomicReadInt16(this.int32, off);
            case 'Uint32': return Atomics.load(this.int32, off >> 2) >>> 0;
            case 'Int32': return Atomics.load(this.int32, off >> 2);
            case 'Float32': return intToFloat(Atomics.load(this.int32, off >> 2));
            case 'Float64':
                throw new Error('Float64 is not supported in the Parameter Buffer. Use Float32.');
            default: return 0;
        }
    }
    /** Read all parameters as a plain object */
    readAll() {
        const result = {};
        for (const [name] of this.fields) {
            result[name] = this.read(name);
        }
        return result;
    }
}
// --- Error Region ---
// The error_code field acts as the publish signal for the error region.
// Writers: write message bytes first, then length, then Atomics.store error_code last.
// Readers: Atomics.load error_code first; if non-zero, length and message are stable.
export function readError(sab, base = 0) {
    const int32 = new Int32Array(sab);
    const view = new DataView(sab);
    // Atomic load on error_code — acts as the acquire fence
    const code = Atomics.load(int32, (base + ERROR_CODE_OFFSET) >> 2) >>> 0;
    if (code === 0)
        return null;
    const len = Math.min(view.getUint32(base + ERROR_LENGTH_OFFSET, true), MAX_ERROR_MESSAGE_LENGTH);
    const bytes = new Uint8Array(sab, base + ERROR_MESSAGE_OFFSET, len);
    return { code, message: new TextDecoder().decode(bytes) };
}
export function writeError(sab, base, code, message) {
    const int32 = new Int32Array(sab);
    const view = new DataView(sab);
    const enc = new TextEncoder().encode(message.slice(0, MAX_ERROR_MESSAGE_LENGTH));
    // Write message bytes and length BEFORE publishing the error code
    new Uint8Array(sab, base + ERROR_MESSAGE_OFFSET, MAX_ERROR_MESSAGE_LENGTH).set(enc);
    view.setUint32(base + ERROR_LENGTH_OFFSET, enc.length, true);
    // Atomic store on error_code — acts as the release fence (publish signal)
    Atomics.store(int32, (base + ERROR_CODE_OFFSET) >> 2, code);
}
export function clearError(sab, base = 0) {
    const int32 = new Int32Array(sab);
    Atomics.store(int32, (base + ERROR_CODE_OFFSET) >> 2, 0);
}
//# sourceMappingURL=param-buffer.js.map